import { forwardRef } from 'react';
import { cn } from '@/lib/cn';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-brand-500 text-white shadow-soft-xs',
    'hover:bg-brand-600 active:bg-brand-700',
    'focus-visible:ring-brand-500/30',
  ),
  secondary: cn(
    'bg-slate-100 text-slate-700',
    'hover:bg-slate-200 active:bg-slate-200/80',
  ),
  ghost: cn(
    'bg-transparent text-slate-600',
    'hover:bg-slate-100 active:bg-slate-200/60',
  ),
  danger: cn(
    'bg-red-500 text-white shadow-soft-xs',
    'hover:bg-red-600 active:bg-red-700',
    'focus-visible:ring-red-500/30',
  ),
  outline: cn(
    'bg-white text-slate-700 border border-slate-200 shadow-soft-xs',
    'hover:bg-slate-50 hover:border-slate-300 active:bg-slate-100',
  ),
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-lg',
  md: 'h-9 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-10 px-5 text-sm gap-2 rounded-xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconRight,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center font-medium',
          'transition-all duration-150 select-none',
          'disabled:opacity-50 disabled:pointer-events-none',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        {...props}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : icon ? (
          <span className="shrink-0 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>
        ) : null}
        {children}
        {iconRight && !loading && (
          <span className="shrink-0 [&>svg]:w-4 [&>svg]:h-4">{iconRight}</span>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
